import { Locale } from '@/shared/constants/locale';
import { publicStringsPath } from '@/shared/constants/api-paths';
import { normalizeApiBaseUrl } from '@/utils/api-url';
import { fetchPublicBootResource } from '@/utils/public-boot-fetch';
import { markBootConfigUnreachable } from '@/utils/boot-config-source';

let cachedPublicStrings: Record<string, string> = {};
let cachedPublicLocale: string | null = null;

const ALLOWED_LOCALES = new Set(Object.values(Locale));

function normalizeLocale(locale: unknown): string | null {
  if (!locale) {
    return null;
  }

  const normalized = String(locale).split('-')[0].toLowerCase();

  return ALLOWED_LOCALES.has(normalized as Locale) ? normalized : null;
}

export function resolvePreAuthLocale({
  publicSettings = {},
  fallbackLocale = 'en',
}: {
  publicSettings?: Record<string, unknown>;
  fallbackLocale?: string;
} = {}): string | null {
  if (typeof window !== 'undefined') {
    const urlLocale = new URLSearchParams(window.location.search).get('locale');

    if (urlLocale) {
      return normalizeLocale(urlLocale) ?? normalizeLocale(fallbackLocale);
    }
  }

  const fromPublic = publicSettings.defaultLocale ?? publicSettings.default_locale ?? publicSettings.fallback_locale;

  if (fromPublic) {
    return normalizeLocale(fromPublic) ?? normalizeLocale(fallbackLocale);
  }

  if (typeof navigator !== 'undefined' && navigator.language) {
    return normalizeLocale(navigator.language) ?? normalizeLocale(fallbackLocale);
  }

  return normalizeLocale(fallbackLocale) ?? 'en';
}

export async function fetchPublicStrings(apiBaseUrl: unknown, locale: unknown): Promise<Record<string, string>> {
  const normalizedLocale = normalizeLocale(locale);

  if (!normalizedLocale) {
    console.warn('[settings] Public strings skipped — locale could not be resolved; using bundled strings.');
    return {};
  }

  const base = normalizeApiBaseUrl(apiBaseUrl);

  if (!base) {
    console.warn('[settings] Public strings skipped — HELIX_API_URL is unset; using bundled strings.');
    return {};
  }

  const url = `${base}${publicStringsPath(normalizedLocale)}`;
  const { response, outcome } = await fetchPublicBootResource(url, {
    label: `GET /public/strings/${normalizedLocale}`,
  });

  if (outcome === 'unreachable' || !response) {
    markBootConfigUnreachable(`GET /public/strings/${normalizedLocale} unreachable`);
    return {};
  }

  if (!response.ok) {
    console.warn(`[settings] GET /public/strings/${normalizedLocale} returned HTTP ${response.status}; using bundled strings.`);
    return {};
  }

  try {
    const data = await response.json();
    return data && typeof data === 'object' ? (data as Record<string, string>) : {};
  } catch (error) {
    console.warn(`[settings] GET /public/strings/${normalizedLocale} returned invalid JSON; using bundled strings:`, error);
    return {};
  }
}

export async function bootstrapPublicStrings(
  apiBaseUrl: unknown,
  options: { publicSettings?: Record<string, unknown>; fallbackLocale?: string } = {},
): Promise<Record<string, string>> {
  // Always fetch anonymously on cold boot — authenticated sessions still need
  // public labels for login bounce / error surfaces and must not skip this path.
  cachedPublicLocale = resolvePreAuthLocale(options);
  cachedPublicStrings = await fetchPublicStrings(apiBaseUrl, cachedPublicLocale);
  return cachedPublicStrings;
}

export function applyBootstrappedPublicStrings(applyPublicStrings: (locale: string, strings: Record<string, string>) => void) {
  if (!cachedPublicLocale || !Object.keys(cachedPublicStrings).length) {
    return;
  }

  applyPublicStrings(cachedPublicLocale, cachedPublicStrings);
}

export function getBootstrappedPublicStringsState() {
  return {
    locale: cachedPublicLocale,
    strings: cachedPublicStrings,
  };
}
